import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CanvasService } from '../canvas.service';
import {
  CanvasEvents,
  CanvasLoaderType,
  CanvasStringConstants,
  CanvasViewTypes,
  ChatTypes,
  ToolbarOriginType,
} from '../canvas.model';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { cloneDeep } from 'lodash';
import { HttpResponse } from '@angular/common/http';
import { CanvasHelperService } from '../canvas-helper.service';

@Component({
  selector: 'app-canvas-content-editor',
  templateUrl: './canvas-content-editor.component.html',
  styleUrls: ['./canvas-content-editor.component.scss'],
})
export class CanvasContentEditorComponent implements OnInit, OnDestroy {
  readonly viewTypes = CanvasViewTypes;
  viewType = CanvasViewTypes.Canvas;
  private editorRef?: ElementRef;
  @ViewChild('editor') set editorContent(ref: ElementRef | undefined) {
    if (ref) {
      this.editorRef = ref;
      this.setupEditorListener();
    }
  }

  document!: any;
  currentVersionDocuemnt!: any;
  loaderTypes = CanvasLoaderType;

  chatTypes = ChatTypes;
  isCanvasDirty = false;
  isSavingDocument = false;
  isDownloadingDocument = false;
  toolbarPosition = {
    x: 0,
    y: 0,
    origin: ToolbarOriginType.BY_MOUSE_SELECTION,
  };
  userSelection: string = '';
  isContentSelectionToolbarActive = false;
  private contentEditorSub!: Subscription;
  private onContentFetchedSub!: Subscription;

  constructor(
    public canvasService: CanvasService,
    private readonly sanitize: DomSanitizer,
    public helperService: CanvasHelperService
  ) {}

  ngOnInit(): void {
    this.onContentFetchedSub =
      this.canvasService.onCanvasDocumentFetched$.subscribe((update) => {
        if (update.type === CanvasEvents.CANVAS_DOCUMENT_FETCHED) {
          this.document = cloneDeep(this.canvasService.outlineDocument);
          this.processDocumentContent();
          this.setCleanCanvas();
        } else if (update.type === CanvasEvents.CANVAS_DOCUMENT_FETCHED_ERROR) {
          this.isCanvasDirty = false;
          this.document = null;
          this.currentVersionDocuemnt = null;
          this.canvasService.isLoadingDocument = false;
        }
      });
  }

  saveDocument() {
    if (this.editorRef?.nativeElement?.innerHTML) {
      this.isSavingDocument = true;
      this.canvasService.setAgentWorking(
        true,
        CanvasStringConstants.TEMPORARY_DOCUMENT_ID,
        CanvasStringConstants.TEMPORARY_DOCUMENT_ID
      );
      // TODO: Remove TEMPORARY_DOCUMENT_ID and use actual ids
      this.canvasService
        .getMarkdownContent(this.editorRef?.nativeElement.innerHTML)
        .then((mdContent) => {
          if (mdContent) {
            this.canvasService
              .saveDocument(
                {
                  title: this.currentVersionDocuemnt.title,
                  content: mdContent,
                },
                this.document._id
              )
              .then((success) => {
                this.isCanvasDirty = false;
                this.isSavingDocument = false;
                this.canvasService.setAgentWorking();
              })
              .catch((error) => {
                this.isCanvasDirty = false;
                this.isSavingDocument = false;
                this.canvasService.setAgentWorking();
              });
          } else {
            console.error('Failed to convert content to Markdown');
            this.canvasService.setAgentWorking();
          }
        })
        .catch((error) => {
          this.isSavingDocument = false;
          this.canvasService.setAgentWorking();
        })
        .finally(() => {});
    }
  }

  onMouseUp(event: MouseEvent): void {
    this.isContentSelectionToolbarActive = false;

    if (this.isContentReadOnly()) {
      return;
    }
    setTimeout(() => {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        this.toolbarPosition = this.helperService.positionToolbar(
          selection,
          this.editorRef
        );
        this.isContentSelectionToolbarActive = true;

        document.addEventListener(
          'mousedown',
          this.hideToolbarOnClickOutside.bind(this)
        );
      } else {
        this.removeUserHiglights();
      }
    }, 100);
  }

  setToolbarChat(): void {
    this.getUserSelectedContent();
    this.applyStyle();
  }

  applyStyle(className: string = 'custom-highlight'): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const commonAncestor = range.commonAncestorContainer as HTMLElement;

    const isWithinBlockElement =
      this.helperService.isWithinBlockElement(commonAncestor);
    const textNodes = this.helperService.getTextNodesInRange(range);

    textNodes.forEach((textNode) => {
      const parent = textNode.parentElement;
      if (!parent) return;

      this.helperService.handleClassApplication(className, textNode, parent);
    });

    if (isWithinBlockElement) {
      this.helperService.ensureBlockStructure(commonAncestor);
    }

    if (className != 'custom-highlight') {
      this.clearHighlights();
      this.clearUserSelection();
    }

    this.updateDocumentContent(this.editorRef?.nativeElement.innerHTML);
  }

  onSectionTitleClick(event: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();

    const target = event.target as HTMLElement;
    const trigger = target.closest('[data-action="heading-icon"]');

    if (trigger) {
      const block = trigger.getAttribute('id');
      if (block) {
        this.clearUserSelection();
        this.clearHighlights();

        setTimeout(() => {
          const elements = document.querySelector(`[data-block="${block}"]`);
          if (elements) {
            elements.classList.add('active-highlight');
            this.getUserSelectedContent(elements.innerHTML);
          }
        }, 50);

        setTimeout(() => {
          let dialogPosition =
            this.helperService.getSectionDialogPosition(event);

          this.toolbarPosition = {
            x: dialogPosition.x,
            y: dialogPosition.y,
            origin: ToolbarOriginType.BY_SECTION_TITLE_CLICK,
          };
          this.isContentSelectionToolbarActive = true;

          document.addEventListener(
            'mousedown',
            this.hideToolbarOnClickOutside.bind(this)
          );
        });
      }
    }
  }

  clearHighlights() {
    const highlighted = document.querySelectorAll('.active-highlight');
    highlighted?.forEach((el) => el.classList.remove('active-highlight'));
  }

  clearUserSelection() {
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  }

  onKeyDown(event: KeyboardEvent): void {}

  sendCanvasQuery(query: string, event?: MouseEvent): void {
    if (
      !this.helperService.checkIfConversationAllowed(
        this.canvasService.conversationSettings,
        this.canvasService.currentChatMessages
      )
    ) {
      return;
    }

    this.confirmSaveDirtyCanvas().then((savedChanges) => {
      if (query?.length > 0 && this.userSelection?.length > 0) {
        this.canvasService.sendCanvasQuery(
          {
            type: this.chatTypes.CANVAS_UPDATE,
            selected_text: this.userSelection,
            utterance: query,
            version: this.currentVersionDocuemnt?.version ?? 0,
          },
          this.document._id
        );
        this.hideToolbarOnClickOutside(event);
      }
    });
  }

  confirmSaveDirtyCanvas(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isCanvasDirty) {
        this.helperService
          .showConfirmationDialog()
          .then((data: any) => {
            this.saveDocument();
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      } else {
        resolve(true);
      }
    });
  }

  confirmDiscardDirtyCanvas(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isCanvasDirty) {
        this.helperService
          .showConfirmationDiscardDialog()
          .then((data: any) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      } else {
        resolve(true);
      }
    });
  }

  copyContent(): void {
    this.canvasService.copyResponse(this.editorRef);
  }

  private setupEditorListener(): void {
    if (this.editorRef) {
      this.contentEditorSub = fromEvent(this.editorRef.nativeElement, 'input')
        .pipe(
          debounceTime(500),
          map(() => this.editorRef?.nativeElement.innerHTML)
        )
        .subscribe((content) => {
          this.isCanvasDirty = true;
          this.isContentSelectionToolbarActive = false;
        });
    }
  }

  private getUserSelectedContent(richTextData: any = null) {
    this.userSelection = '';
    this.canvasService
      .getMarkdownContent(richTextData ?? this.helperService.getUserHiglights())
      .then((mdContent) => {
        this.userSelection = mdContent ?? '';
      })
      .catch((error) => {
        console.log('Error converting content to Markdown:', error);
      })
      .finally(() => {});
  }

  private removeUserHiglights() {
    const spans =
      this.editorRef?.nativeElement.querySelectorAll(`.custom-highlight`);
    spans.forEach((span: any) => {
      const parent = span.parentNode;
      while (span.firstChild) {
        parent?.insertBefore(span.firstChild, span);
      }
      parent?.removeChild(span);
    });
  }

  private hideToolbarOnClickOutside(event: MouseEvent | undefined): void {
    const toolbarElement = document.querySelector('.toolbar');
    if (toolbarElement && !toolbarElement.contains(event?.target as Node)) {
      this.isContentSelectionToolbarActive = false;

      this.removeUserHiglights();
      this.clearHighlights();
      this.clearUserSelection();

      document.removeEventListener(
        'mousedown',
        this.hideToolbarOnClickOutside.bind(this)
      );
    }
  }

  private updateDocumentContent(content: string): void {
    this.currentVersionDocuemnt.convertedContent =
      this.sanitize.bypassSecurityTrustHtml(content);
  }

  private processDocumentContent() {
    if (this.document?.versions?.length > 0) {
      const processedVersions = [];
      for (let i = 0; i < this.document.versions?.length; i++) {
        const version = this.document.versions[i];
        const convertedContent =
          this.canvasService.convertDocuemntContentToHtml(
            {
              content: version.content,
              type: this.document.type,
            },
            i === this.document.versions.length - 1 &&
              !this.canvasService.isSessionReadOnly
          );

        if (convertedContent) {
          version.convertedContent =
            this.sanitize.bypassSecurityTrustHtml(convertedContent);
        }
        processedVersions.push(version);
      }
      this.document.versions = processedVersions;
    }
  }

  setCleanCanvas(value: number | null = null): void {
    this.currentVersionDocuemnt =
      this.document?.versions[value ?? this.document?.versions?.length - 1] ??
      null;
    if (this.viewType === CanvasViewTypes.Comparison) {
      this.getComparedVersionContent();
    }
    this.isCanvasDirty = false;
    this.canvasService.isLoadingDocument = false;
  }

  private clearCanvas() {
    this.document = null;
    this.currentVersionDocuemnt = null;
    this.isCanvasDirty = false;
    this.isContentSelectionToolbarActive = false;
    this.userSelection = '';
    this.canvasService.isLoadingDocument = false;
    this.viewType = CanvasViewTypes.Canvas;
  }

  isPreviousVersionAvailable(): boolean {
    return (
      this.document.versions?.length > 1 &&
      this.currentVersionDocuemnt.version > 1
    );
  }

  isNextVersionAvailable(): boolean {
    return (
      this.document.versions?.length > 1 &&
      this.currentVersionDocuemnt.version < this.document.versions.length
    );
  }

  previousVersion() {
    this.confirmDiscardDirtyCanvas()
      .then((discardChanges) => {
        if (discardChanges) {
          let currentVersion =
            cloneDeep(this.currentVersionDocuemnt.version) - 2;
          this.setCleanCanvas(currentVersion);
        }
      })
      .catch((error) => {});
  }

  nextVersion() {
    let currentVersion = cloneDeep(this.currentVersionDocuemnt.version);
    this.setCleanCanvas(currentVersion);
  }

  isContentReadOnly(): boolean {
    return (
      this.canvasService.isSessionReadOnly ||
      this.document?.type == this.chatTypes.SCORECARD ||
      this.currentVersionDocuemnt?.version != this.document?.versions?.length ||
      this.isContentSelectionToolbarActive ||
      this.canvasService.isAgentWorking()
    );
  }

  compareVersion() {
    if (this.viewType === CanvasViewTypes.Comparison) {
      this.viewType = CanvasViewTypes.Canvas;
    } else {
      this.viewType = CanvasViewTypes.Comparison;
      this.clearUserSelection();
      this.getComparedVersionContent();
    }
  }

  getComparedVersionContent() {
    this.currentVersionDocuemnt.convertedCompareContent =
      this.sanitize.bypassSecurityTrustHtml(
        this.canvasService.getSanitizedHtmlForCanvasComparison(
          this.canvasService.getConvertedMarkdown(
            this.currentVersionDocuemnt.compare_with_previous ?? ''
          )
        )
      );
  }

  checkIfCompareVersionAvailable() {
    return (
      this.currentVersionDocuemnt?.compare_with_previous?.length > 0 &&
      (this.currentVersionDocuemnt?.compare_with_previous?.includes('<<<') ||
        this.currentVersionDocuemnt?.compare_with_previous?.includes('[[['))
    );
  }

  exportDocument() {
    this.isDownloadingDocument = true;
    this.canvasService.exportDocument(this.document._id).subscribe({
      next: (response: HttpResponse<ArrayBuffer>) => {
        this.isDownloadingDocument = false;
        const fileName = `document-${this.document._id}`;
        const mimeType =
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        const blob = new Blob([response.body!], { type: mimeType });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error: any) => {
        this.isDownloadingDocument = false;
        console.error('Error downloading document:', error);
        this.canvasService.showErrorMessage('Failed to download document');
      },
    });
  }

  ngOnDestroy(): void {
    this.clearCanvas();
    this.canvasService.closeOutline(false);
    this.contentEditorSub?.unsubscribe();
    this.onContentFetchedSub?.unsubscribe();
  }
}
